import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} [headerDrawerContainer] - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  #megaMenuHydrationObserver = new MutationObserver(() => {
    this.hydrateMegaMenuPromos();
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    const drawer = this.refs.headerDrawerContainer;
    const menu = this.refs.headerMenu;

    if (!drawer || !menu) return;

    if (hideMenu) {
      drawer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      menu.classList.add('hidden');
    } else {
      drawer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      menu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      const isAtTop = this.getBoundingClientRect().top >= 0;

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#megaMenuHydrationObserver.observe(this, { childList: true, subtree: true });
    this.hydrateMegaMenuPromos();
    requestAnimationFrame(() => this.hydrateMegaMenuPromos());
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#megaMenuHydrationObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }

  hydrateMegaMenuPromos() {
    this.#hydrateMegaMenuPromos();
  }

  /**
   * Theme blocks rendered with `content_for 'blocks'` are not available through
   * the static header menu block, so Mega menu promo templates are inserted after render.
   */
  #hydrateMegaMenuPromos() {
    const templates = [...this.querySelectorAll('template[data-mega-menu-template]')];
    const drawerTemplates = [...this.querySelectorAll('template[data-mega-menu-drawer-template]')];

    if (templates.length) {
      this.#hydrateDesktopMegaMenuPromos(templates);
    }

    if (drawerTemplates.length) {
      this.#hydrateDrawerMegaMenuPromos(drawerTemplates);
    }
  }

  /**
   * @param {HTMLTemplateElement[]} templates
   */
  #hydrateDesktopMegaMenuPromos(templates) {
    const menuItems = this.querySelectorAll('header-menu .menu-list__list-item');

    menuItems.forEach((menuItem) => {
      const menuLink = menuItem.querySelector(':scope > .menu-list__link[ref="menuitem"]');
      if (!(menuLink instanceof HTMLElement)) return;

      const template = findMegaMenuTemplate(templates, menuLink.textContent);
      if (!template) return;

      const grid = menuItem.querySelector(':scope > .menu-list__submenu .mega-menu__grid');
      if (!(grid instanceof HTMLElement)) return;

      this.#appendDesktopMegaMenuPromo(grid, template);
    });
  }

  /**
   * @param {HTMLElement} grid
   * @param {HTMLTemplateElement} template
   */
  #appendDesktopMegaMenuPromo(grid, template) {
    if (grid.querySelector(':scope > .mega-menu__content--promos')) return;

    const promo = template.content.firstElementChild?.cloneNode(true);
    if (!(promo instanceof HTMLElement)) return;

    const promoCount = Math.min(parseInt(template.dataset.promoCount || '0', 10), 2);
    const menuList = grid.querySelector(':scope > .mega-menu__list');

    if (promoCount > 0 && menuList instanceof HTMLElement) {
      const gridStyle = getComputedStyle(grid);
      const desktopColumns = parseInt(gridStyle.getPropertyValue('--menu-columns-desktop'), 10) || 6;
      const tabletColumns = parseInt(gridStyle.getPropertyValue('--menu-columns-tablet'), 10) || 4;
      const desktopTextColumns = promoCount === 1 ? 4 : 3;

      menuList.classList.add('mega-menu__list--with-promos');
      grid.classList.add('mega-menu__grid--with-promos');
      menuList.style.setProperty('--menu-columns-desktop', `${Math.max(desktopColumns - promoCount, 1)}`);
      menuList.style.setProperty('--menu-columns-tablet', `${Math.max(tabletColumns - promoCount, 1)}`);
      menuList.style.setProperty('--mega-menu-text-columns-desktop', `${desktopTextColumns}`);
      menuList.style.setProperty(
        '--mega-menu-text-column-width',
        `calc((100% - (var(--mega-menu-text-column-gap) * ${desktopTextColumns - 1})) / ${desktopTextColumns})`
      );
    }

    grid.append(promo);
  }

  /**
   * @param {HTMLTemplateElement[]} templates
   */
  #hydrateDrawerMegaMenuPromos(templates) {
    const drawerItems = this.querySelectorAll('.menu-drawer__menu-item--mainlist');

    drawerItems.forEach((menuItem) => {
      const template = findMegaMenuTemplate(templates, menuItem.textContent);
      if (!template) return;

      const listItem = menuItem.closest('.menu-drawer__list-item');
      const details = menuItem.closest('details');
      const target = details?.querySelector(':scope > .menu-drawer__submenu .menu-drawer__inner-submenu') || details || listItem;
      const animationSource = listItem instanceof HTMLElement ? listItem : menuItem;
      const animationIndex = getComputedStyle(animationSource).getPropertyValue('--menu-drawer-animation-index').trim();

      if (!(target instanceof HTMLElement)) return;
      if (target.querySelector(':scope > .menu-drawer__mega-menu-promos')) return;

      const promo = template.content.firstElementChild?.cloneNode(true);
      if (promo instanceof HTMLElement) {
        if (animationIndex) {
          promo.style.setProperty('--menu-drawer-animation-index', animationIndex);
        }

        target.append(promo);
      }
    });
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/**
 * @param {HTMLTemplateElement[]} templates
 * @param {string | null | undefined} label
 * @returns {HTMLTemplateElement | undefined}
 */
function findMegaMenuTemplate(templates, label) {
  const labelText = label?.trim();
  if (!labelText) return undefined;

  const labelDowncase = labelText.toLowerCase();
  const labelHandle = handleize(labelText);

  return templates.find((template) => {
    const menuItem = template.dataset.menuItem?.trim() || '';
    const menuHandle = template.dataset.menuHandle || handleize(menuItem);

    return menuItem.toLowerCase() === labelDowncase || menuHandle === labelHandle;
  });
}

/**
 * Mirrors the Liquid handle used for editor-entered menu item names closely enough
 * for ASCII menu titles such as "Shop".
 * @param {string} value
 * @returns {string}
 */
function handleize(value) {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  if (header instanceof HTMLElement && typeof header.hydrateMegaMenuPromos === 'function') {
    header.hydrateMegaMenuPromos();
  }

  const updateHeaderGroupHeight = () => {
    document.body.style.setProperty('--header-group-height', `${calculateHeaderGroupHeight(header, headerGroup)}px`);
  };

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(updateHeaderGroupHeight);

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
          updateHeaderGroupHeight();
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
